import React from "react";
import {
  AssetType,
  MobileMetadata,
  AssetFormData,
} from "../../../types/asset-types";
import {
  BaseAssetFormProps,
  AssetFormHeader,
  FormField,
  TextInput,
  TextArea,
  SelectInput,
  AIConversationFields,
} from "./asset-form-base";

const VEHICLE_TYPE_OPTIONS = [
  { value: "bus", label: "Bus" },
  { value: "truck", label: "Truck" },
  { value: "car", label: "Car" },
  { value: "train", label: "Train" },
  { value: "other", label: "Other" },
];

export function MobileAssetForm({
  data,
  onChange,
  errors,
  disabled,
}: BaseAssetFormProps) {
  const metadata = (data.metadata as Partial<MobileMetadata>) || {};

  const handleMetadataChange = (field: keyof MobileMetadata, value: any) => {
    const newMetadata = {
      ...metadata,
      [field]: value,
    };
    onChange({
      ...data,
      metadata: newMetadata,
    });
  };

  const handleAIContentChange = (aiData: any) => {
    const newMetadata = {
      ...metadata,
      ...aiData,
    };
    onChange({
      ...data,
      metadata: newMetadata,
    });
  };

  return (
    <div className="space-y-6">
      <AssetFormHeader
        type={AssetType.MOBILE}
        description="Create a mobile asset like a vehicle wrap, transit advertisement, or moving display."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="Vehicle ID"
          required
          error={errors?.["metadata.vehicleId"]}
          description="Unique identifier for the vehicle (license plate, fleet number, etc.)"
        >
          <TextInput
            value={metadata.vehicleId || ""}
            onChange={(value) => handleMetadataChange("vehicleId", value)}
            placeholder="ABC-123 or Fleet-001"
            disabled={disabled}
            required
          />
        </FormField>

        <FormField
          label="Vehicle Type"
          required
          error={errors?.["metadata.vehicleType"]}
          description="Type of vehicle carrying the asset"
        >
          <SelectInput
            value={metadata.vehicleType || ""}
            onChange={(value) => handleMetadataChange("vehicleType", value)}
            options={VEHICLE_TYPE_OPTIONS}
            placeholder="Select vehicle type"
            disabled={disabled}
            required
          />
        </FormField>
      </div>

      <FormField
        label="Operator"
        required
        error={errors?.["metadata.operator"]}
        description="Company or individual operating the vehicle"
      >
        <TextInput
          value={metadata.operator || ""}
          onChange={(value) => handleMetadataChange("operator", value)}
          placeholder="City Transit Authority, ABC Delivery Co."
          disabled={disabled}
          required
        />
      </FormField>

      <FormField
        label="Route"
        error={errors?.["metadata.route"]}
        description="Regular route or path taken by the vehicle"
      >
        <TextInput
          value={metadata.route || ""}
          onChange={(value) => handleMetadataChange("route", value)}
          placeholder="Route 42, Downtown Loop, Highway 101"
          disabled={disabled}
        />
      </FormField>

      <FormField
        label="Schedule"
        error={errors?.["metadata.schedule"]}
        description="Operating schedule or frequency"
      >
        <TextArea
          value={metadata.schedule || ""}
          onChange={(value) => handleMetadataChange("schedule", value)}
          placeholder="Monday-Friday 6AM-10PM, Every 15 minutes during peak hours"
          disabled={disabled}
          rows={2}
        />
      </FormField>

      <FormField
        label="Tracking Method"
        required
        error={errors?.["metadata.trackingMethod"]}
        description="How this mobile asset will be tracked or monitored"
      >
        <TextInput
          value={metadata.trackingMethod || ""}
          onChange={(value) => handleMetadataChange("trackingMethod", value)}
          placeholder="GPS tracking, Manual check-ins, Route monitoring"
          disabled={disabled}
          required
        />
      </FormField>

      <AIConversationFields
        data={metadata}
        onChange={handleAIContentChange}
        errors={errors}
        disabled={disabled}
      />
    </div>
  );
}
